import { Controller, useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";

import { AllowedDomainsEditor } from "./AllowedDomainsEditor";
import { ChatPersonalizationEditor } from "./ChatPersonalizationEditor";
import { ISetting, Setting } from "./Setting";

const SETTINGS: ISetting[] = [
    {
        name: "field_extraction_enabled",
        title: "Field Extraction",
        description: "Automatically extract and analyze fields from scopes.",
    },
    {
        name: "ai_clause_comparison_enabled",
        title: "AI Clause Comparison",
        description:
            "Automatically compare clauses using AI within comparisons.",
    },
    {
        name: "ai_chat_enabled",
        title: "AI Chat",
        description: "Enable AI-powered chat features within the organization.",
    },
    {
        name: "scope_reasoning_enabled",
        title: "Scope Reasoning",
        description:
            "Use metadata-aware reasoning when analyzing clauses. When disabled, clause matching uses natural language similarity only.",
    },
    {
        name: "context_enrichment_enabled",
        title: "Context Enrichment",
        description:
            "Show Polly's 'refine your answer' card at the end of chat responses so users can tailor follow-ups with quick context chips.",
    },
    {
        name: "share_polly_enabled",
        title: "Share Polly",
        description:
            "Allow members to share Polly conversations with teammates and external recipients. Disabling hides the share trigger and rejects new shares.",
    },
    {
        name: "output_templates_enabled",
        title: "Output Templates",
        description:
            "Allow members to access and manage PDF and spreadsheet templates in the Templates library.",
    },
    {
        name: "reports_enabled",
        title: "Reports",
        description:
            "Allow Polly to prepare PDF reports from documents in chat and let members try a failed report again. Reports already prepared stay visible when this is off.",
    },
    {
        name: "scopes_enabled",
        title: "Scopes",
        description:
            "Show Scopes, the Clause Library and Checklists to this organization. Turning this off hides them and Polly's scope tools; no data is deleted.",
    },
    {
        name: "clause_matching_enabled",
        title: "Clause Matching (Experimental)",
        description:
            "Use the new clause matching engine instead of the default clause analysis service. When disabled, the battle-tested analysis pipeline is used.",
    },
    {
        name: "policy_primer_enabled",
        title: "Policy Primer",
        description:
            "Run Polly's policy pre-read before review conversations in this organization, giving it a directory of attached policies and clause candidates up front.",
    },
];

interface IProps {
    isLoading?: boolean;
}

export const SettingsForm = ({ isLoading = false }: IProps) => {
    const { control } = useFormContext();
    return (
        <div className="flex w-full flex-col gap-4">
            {SETTINGS.map((setting) => (
                <Setting
                    key={setting.name}
                    {...setting}
                    isLoading={isLoading}
                />
            ))}

            <Separator />

            <Controller
                name="allowed_domains"
                control={control}
                render={({ field }) => (
                    <AllowedDomainsEditor
                        value={field.value ?? []}
                        onChange={field.onChange}
                        disabled={isLoading}
                    />
                )}
            />

            <Separator />

            <ChatPersonalizationEditor isLoading={isLoading} />

            <Separator />

            <div className="flex justify-end">
                <Button type="submit" disabled={isLoading}>
                    {isLoading && <Spinner />}
                    Save Settings
                </Button>
            </div>
        </div>
    );
};
